import React from 'react';
import { Affix, DatePicker, Flex, Typography, theme } from 'antd';
import dayjs from 'dayjs';

import {
  useDashboardDateRange,
  describeDashboardReportingPeriod,
  dashboardQuickRangeOptions,
  computeQuickRangeDatesForPreset
} from '../../context/DashboardDateRangeContext';

const { Text } = Typography;

/**
 * Thin sticky strip: summary + range picker (preset list lives only in the picker dropdown).
 */
const DashboardDateRangeToolbar = ({
  affixOffsetTop = 0,
  prepend = null
}) => {
  const { token } = theme.useToken();
  const { startDate, endDate, setRange } = useDashboardDateRange();

  const summary = React.useMemo(
    () => describeDashboardReportingPeriod(startDate, endDate),
    [startDate, endDate]
  );

  const compactDates = React.useMemo(() => {
    const s = dayjs(startDate);
    const e = dayjs(endDate);
    if (s.year() === e.year()) {
      return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
    }
    return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
  }, [startDate, endDate]);

  const presets = React.useMemo(
    () =>
      dashboardQuickRangeOptions.map(({ label, id }) => {
        const { startDate: sd, endDate: ed } = computeQuickRangeDatesForPreset(id);
        return {
          label,
          value: [dayjs(sd), dayjs(ed)]
        };
      }),
    []
  );

  const pickerValue = React.useMemo(
    () => [dayjs(startDate), dayjs(endDate)],
    [startDate, endDate]
  );

  const fullDateSummary = `${dayjs(startDate).format('MMM D, YYYY')} – ${dayjs(endDate).format('MMM D, YYYY')}`;
  const showCompactDatesAfterSummary = summary !== fullDateSummary;

  const barStyle = {
    marginBottom: 20,
    padding: `10px 0px`,
    background: token.colorBgLayout,
    borderBottom: `1px dashed ${token.colorBorder}`,
    transition: 'box-shadow 0.15s ease',
    borderRadius: 0
  };

  return (
    <Affix offsetTop={affixOffsetTop}>
      <div style={barStyle}>
        <Flex
          justify="space-between"
          align="center"
          wrap="wrap"
          gap={token.paddingXS}
          style={{ width: '100%' }}
        >
          <Flex align="center" wrap="wrap" gap={token.paddingXS}>
            {prepend ? (
              <>
                {prepend}
                <Text type="secondary" style={{ userSelect: 'none' }}>
                  |
                </Text>
              </>
            ) : null}
            <span style={{ margin: 0 }}>
              <Text strong style={{ fontSize: token.fontSize }}>
                {summary}
              </Text>
              {showCompactDatesAfterSummary ? (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {' '}
                  · {compactDates}
                </Text>
              ) : null}
            </span>
          </Flex>
          <RangePickerWithPresets
            value={pickerValue}
            presets={presets}
            size="small"
            onChange={(next) => {
              if (!next || !next[0] || !next[1]) return;
              setRange({
                startDate: next[0].format('YYYY-MM-DD'),
                endDate: next[1].format('YYYY-MM-DD')
              });
            }}
          />
        </Flex>
      </div>
    </Affix>
  );
};

function RangePickerWithPresets({ value, presets, onChange, size }) {
  return (
    <DatePicker.RangePicker
      value={value}
      presets={presets}
      size={size}
      onChange={(dates) => onChange(dates)}
      format="MMM D, YYYY"
      allowClear={false}
    />
  );
}

export default DashboardDateRangeToolbar;
